import React from "react";
import { useTranslation } from "react-i18next";
import { getEnumTKey } from "@/utils/strings";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { PartnerStatus } from "@/pages/partners/types";
import { partnerStatusColors } from "@/pages/partners/contacts/helpers";
import Text from "@/components/ui/text";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";

interface Props {
  partnerContact?: PartnerContactData;
  onUpdateField: (field: string, newValue: string | number) => void;
  className?: string;
  itemClassName?: string;
}

const PartnerContactTopFields: React.FC<Props> = ({
  partnerContact,
  onUpdateField,
  className,
  itemClassName
}) => {
  const { t } = useTranslation();

  const {
    options: partnerOptions,
    onSearch: onPartnerOptionsSearch,
    isLoading: isLoadingPartners,
    searchTerm: partnerSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PARTNER,
    selectedId: partnerContact?.partner || "",
    alwaysEnabled: true
  });

  const renderStatus = () => (
    <div className="flex items-center gap-1 pr-2">
      <IconContainer
        style={{
          color: partnerStatusColors[partnerContact?.status as PartnerStatus]
        }}
      >
        <Bullet />
      </IconContainer>
      <Text variant="body">
        {t(
          getEnumTKey(
            "partnercontacts",
            "statusValues",
            PartnerStatus,
            partnerContact?.status as number
          )
        )}
      </Text>
    </div>
  );

  const fieldsConfig: FieldsConfig = {
    partner: {
      type: "select",
      icon: "partner",
      entity: EntityType.PARTNER,
      options: partnerOptions,
      searchTerm: partnerSearchTerm,
      showSearch: true,
      onSearch: onPartnerOptionsSearch,
      isLoading: isLoadingPartners,
      popoverClassName: "max-w-[30vw]"
    },
    jobTitle: {
      type: "editableInput",
      icon: "job",
      initialValue: partnerContact?.jobTitle || ""
    },
    ...(partnerContact?.status != null && {
      status: {
        type: "nonEditable",
        customComponent: renderStatus()
      }
    })
  };

  return (
    <TopFields
      entity={partnerContact}
      fieldsConfig={fieldsConfig}
      onUpdateField={onUpdateField}
      className={className}
      itemClassName={itemClassName}
    />
  );
};

export default PartnerContactTopFields;
